import { AttachmentType } from '@issuemanager/constants';
import { z } from 'zod';
import { optionalDate } from './common';
import { MinimalUserEntity, minimalUserSchema } from './user';

// Represents the attachment table schema 1-to-1 mapping with database columns
export const attachmentTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  hb_drive_id: z.string(),
  size: z.number(),
  created_at: z.string().transform((date) => new Date(date)),
  added_by: z.string(),
  deleted_at: optionalDate(),
  organization_id: z.string(),
});

export const attachmentBaseQuerySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    hb_drive_id: z.string(),
    size: z.number(),
    created_at: z.string().transform((date) => new Date(date)),
    added_by_id: z.string(),
    added_by_name: z.string(),
    added_by_email: z.string(),
    added_by_initials: z.string().nullable(),
  })
  .transform((row) => ({
    id: row.id,
    name: row.name,
    size: row.size,
    hbDriveId: row.hb_drive_id,
    createdAt: row.created_at,
    addedBy: {
      id: row.added_by_id,
      name: row.added_by_name,
      email: row.added_by_email,
      initials: row.added_by_initials,
    },
  }));

// Use this schema as a response for the attachment query
export const attachmentQueryResponseSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    hb_drive_id: z.string(),
    size: z.number(),
    created_at: z.string().transform((date) => new Date(date)),
    added_by: minimalUserSchema,
    attachment_type: z.nativeEnum(AttachmentType),
  })
  .transform((attachment) => ({
    id: attachment.id,
    name: attachment.name,
    size: attachment.size,
    hbDriveId: attachment.hb_drive_id,
    createdAt: attachment.created_at,
    addedBy: attachment.added_by,
    attachmentType: attachment.attachment_type,
  }));
export type AttachmentQueryResponseType = z.infer<
  typeof attachmentQueryResponseSchema
>;

export type Attachment = {
  id: string;
  name: string;
  size: number;
  hbDriveId: string;
  createdAt: Date;
  addedBy: MinimalUserEntity;
};
// Define the schema for related data
export const relatedDataSchema = z
  .object({
    attachment_id: z.string(),
    action_id: z.string().optional(),
    action_draft_id: z.string().optional(),
    issue_id: z.string().optional(),
    issue_timeline_id: z.string().optional(),
    issue_code: z.string(),
    attachment_related_table: z.string(),
  })
  .transform((data) => ({
    attachmentId: data.attachment_id,
    actionId: data.action_id,
    actionDraftId: data.action_draft_id,
    issueId: data.issue_id,
    issueTimelineId: data.issue_timeline_id,
    issueCode: data.issue_code,
    attachmentRelatedTable: data.attachment_related_table,
  }));

// Define the schema for the full response
export const attachmentWithRelatedDataSchema = z.object({
  attachment: attachmentQueryResponseSchema, // Use the existing attachment schema
  relatedData: relatedDataSchema, // Include the related data schema
});

// Infer the TypeScript type
export type AttachmentWithRelatedDataType = z.infer<
  typeof attachmentWithRelatedDataSchema
>;
